import "../css/app.css";
import "./bootstrap";

import Loading from "@frontend/js/Components/Shared/Loading/Loading";
import { createInertiaApp, router } from "@inertiajs/react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { useEffect, useRef, useState } from "react";
import { Provider } from "react-redux";

import { store } from "./app/store";


const appName = import.meta.env.VITE_APP_NAME || "Laravel";
const noAppSuffixMarker = "__NO_APP_SUFFIX__";
const adminPages = import.meta.glob("./Pages/**/*.jsx");
const frontendPages = import.meta.glob("../frontend/js/Pages/**/*.jsx");
const globalLoaderDelayMs = 150;

function getInitialPageProps() {
    try {
        const appRoot = document.getElementById("app");
        const serializedPage = appRoot?.dataset?.page;

        if (!serializedPage) {
            return {};
        }

        return JSON.parse(serializedPage)?.props || {};
    } catch {
        return {};
    }
}

function setGlobalTranslator(translations = {}) {
    const dictionary = translations || {};

    window.__SITE_TRANSLATIONS__ = dictionary;
    window.__ = (key) => {
        const value = dictionary?.[key];

        return typeof value === "string" && value !== "" ? value : key;
    };
}

router.on("navigate", (event) => {
    if (event.detail.page.props.csrf_token) {
        axios.defaults.headers.common["X-CSRF-TOKEN"] =
            event.detail.page.props.csrf_token;
    }

    setGlobalTranslator(event?.detail?.page?.props?.translations?.site || {});
});

setGlobalTranslator(getInitialPageProps()?.translations?.site || {});

function GlobalNavigationLoader({ children }) {
    const [pendingOperations, setPendingOperations] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const showTimerRef = useRef(null);

    useEffect(() => {
        const increasePending = () => {
            setPendingOperations((value) => value + 1);
        };

        const decreasePending = () => {
            setPendingOperations((value) => Math.max(0, value - 1));
        };

        const stopStart = router.on("start", () => {
            increasePending();
        });

        const stopFinish = router.on("finish", () => {
            decreasePending();
        });

        const stopError = router.on("error", () => {
            decreasePending();
        });

        window.addEventListener("global-loader:show", increasePending);
        window.addEventListener("global-loader:hide", decreasePending);

        return () => {
            stopStart();
            stopFinish();
            stopError();

            window.removeEventListener("global-loader:show", increasePending);
            window.removeEventListener("global-loader:hide", decreasePending);
        };
    }, []);

    useEffect(() => {
        if (pendingOperations > 0) {
            if (showTimerRef.current) {
                return;
            }

            showTimerRef.current = window.setTimeout(() => {
                setIsVisible(true);
                showTimerRef.current = null;
            }, globalLoaderDelayMs);

            return;
        }

        if (showTimerRef.current) {
            window.clearTimeout(showTimerRef.current);
            showTimerRef.current = null;
        }

        setIsVisible(false);
    }, [pendingOperations]);

    useEffect(
        () => () => {
            if (showTimerRef.current) {
                window.clearTimeout(showTimerRef.current);
                showTimerRef.current = null;
            }
        },
        [],
    );

    return (
        <>
            {children}
            {isVisible && (
                <div
                    className="global-navigation-loader"
                    role="status"
                    aria-live="polite"
                    aria-label="Loading page"
                >
                    <Loading />
                </div>
            )}
        </>
    );
}

function waitForDOM() {
    return new Promise((resolve) => {
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", resolve, {
                once: true,
            });
            return;
        }

        resolve();
    });
}


createInertiaApp({
    title: (title = "") => {
        const shouldSkipSuffix = title.endsWith(noAppSuffixMarker);
        const cleanTitle = shouldSkipSuffix
            ? title.slice(0, -noAppSuffixMarker.length)
            : title;

        if (!cleanTitle) {
            return appName;
        }

        return shouldSkipSuffix ? cleanTitle : `${appName} — ${cleanTitle}`;
    },
    resolve: (name) => {
        const adminPageLoader = adminPages[`./Pages/${name}.jsx`];

        if (adminPageLoader) {
            return adminPageLoader();
        }

        const frontendPageLoader =
            frontendPages[`../frontend/js/Pages/${name}.jsx`];

        if (frontendPageLoader) {
            return frontendPageLoader();
        }

        throw new Error(`Inertia page not found: ${name}`);
    },
    setup({ el, App, props }) {
        // Update CSRF token from Inertia props
        if (props.initialPage.props.csrf_token) {
            axios.defaults.headers.common["X-CSRF-TOKEN"] =
                props.initialPage.props.csrf_token;
        }

        // Keep DOM clean: initial payload is no longer needed after bootstrap.
        el.removeAttribute("data-page");

        const isAdminPage = String(
            props?.initialPage?.component || "",
        ).startsWith("Admin/");
        const appContent = <App {...props} />;
        const contentWithOptionalLoader = isAdminPage ? (
            appContent
        ) : (
            <GlobalNavigationLoader>{appContent}</GlobalNavigationLoader>
        );

        if (import.meta.env.SSR) {
            hydrateRoot(
                el,
                <Provider store={store}>{contentWithOptionalLoader}</Provider>,
            );
            return;
        }
        createRoot(el).render(
            <Provider store={store}>{contentWithOptionalLoader}</Provider>,
        );
    },
    progress: {
        color: "#4B5563",
    },
});
